import React, { useState, useEffect  } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "../../Context/AuthContext";
import './UploadCSV.css'

const API = process.env.REACT_APP_API;

const UploadCSV = ({ type, leagueId: initialLeagueId }) => {
  const {token} = useAuth();

  const [selectedFile, setSelectedFile] = useState(null);
  const [leagueCode, setLeagueCode] = useState("");
  const [leagueId, setLeagueId] = useState(initialLeagueId || "");
  const [leaguesList, setLeaguesList] = useState([]);
  const [stagesList, setStagesList] = useState([]);
  const [divisionsList, setDivisionsList] = useState([]);
  const [division, setDivision] = useState(""); // בית (א / ב)
  const [selectedStage, setSelectedStage] = useState("");
  const [loading, setLoading] = useState(false);

  
  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        const res = await axios.get(`${API}/api/leagues`);
        setLeaguesList(res.data); // מערך של אובייקטים: { code, name }
      } catch (err) {
          console.error(err);
          toast.error("שגיאה בטעינת רשימת הליגות");
      }
    };
    fetchLeagues();
  }, []);

  // --- If an initialLeagueId was passed, wait until leaguesList loaded and map to code/id ---
  useEffect(() => {
    if (!initialLeagueId || !leaguesList.length) return;
    const leagueObj =
      leaguesList.find(l => l._id === initialLeagueId) ||
      leaguesList.find(l => l.code === initialLeagueId); // tolerate both
    if (leagueObj) {
      setLeagueId(leagueObj._id);
      setLeagueCode(leagueObj.code);
      // fetch stages for this league code
      fetchStages(leagueObj.code);
    } else {
        console.error("initialLeagueId provided but not found in leaguesList:", initialLeagueId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLeagueId, leaguesList]);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  // --- Fetch stages helper (by league code) ---
  const fetchStages = async (code) => {
    if (!code) return;
    try {
      const url = `${API}/api/leagues/${encodeURIComponent(code)}/stages`;
      const res = await axios.get(url);
      setStagesList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("fetchStages error:", err.response?.data || err.message);
      toast.error("שגיאה בטעינת שלבים");
    }
  };

  // --- Fetch divisions helper (by leagueId and stage) ---
  const fetchDivisions = async (lid, stageId) => {
    if (!lid || !stageId) return;
    try {
      const url = `${API}/api/rankings/${leagueId}?stage=${encodeURIComponent(stageId)}`;
      const res = await axios.get(url);
      setDivisionsList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("fetchDivisions error:", err.response?.data || err.message);
      // Show helpful toast with server message if present
      const serverMsg = err.response?.data?.error;
      toast.error(serverMsg || "שגיאה בטעינת רשימת הבתים");
    }
  };

  const handleLeagueChange = async (e) => {
    const code = e.target.value;
    setLeagueCode(code);

    // מציאת ה-_id של הליגה לפי הקוד
    const leagueObj = leaguesList.find(lg => String(lg.code) === String(code));
    const id = leagueObj?._id || "";
    setLeagueId(id);

    // איפוס שדות
    setSelectedStage("");
    setStagesList([]);
    setDivisionsList([]);
    setDivision(""); 

    if (!code) return;

    // fetch stages using the code (no need to wait for state update)
    await fetchStages(code);
  };

  const handleStageChange = async (e) => {
    const stageId = e.target.value;
    setSelectedStage(stageId);
    setDivisionsList([]);
    setDivision("");
    
    if (!stageId) return;

    // fetch divisions if ranking type
    if (type === "ranking") {
      // make sure we have leagueId (try to derive from leagueCode if missing)
      let lid = leagueId;
      if (!lid && leagueCode) {
        const leagueObj = leaguesList.find(lg => String(lg.code) === String(leagueCode));
        lid = leagueObj?._id || "";
        if (lid) setLeagueId(lid); // update state for later uploads
      }
      if (!lid) {
        toast.error("לא ניתן לטעון בתים — אין מזהה ליגה (leagueId)");
        return;
      }
      await fetchDivisions(lid, stageId);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("אנא בחר קובץ");
      return;
    }
    if ((type === "matches" && (!leagueCode || !selectedStage)) ||
        (type === "ranking" && (!leagueId || !selectedStage || !division))) {
      return toast.error("אנא בחר את כל השדות הנדרשים");
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("csv", selectedFile);

    try {
      let url = "";
      
      // העלאת משחקים קרובים
      if (type === "upcoming") url = `${API}/api/matches/upcoming`;
      // העלאת משחקי ליגה
      else if (type === "matches") url = `${API}/api/matches/${encodeURIComponent(leagueId)}/${encodeURIComponent(selectedStage)}`;
      // העלאת קבצי דירוג
      else if (type === "ranking") url = `${API}/api/rankings/upload/${encodeURIComponent(leagueId)}/${encodeURIComponent(selectedStage)}/${encodeURIComponent(division)}`;

      const res = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${token}`,
        },
      });

      toast.success(res.data.message || "הקובץ הועלה בהצלחה");
      setSelectedFile(null);
      setSelectedStage("");
      setDivision("");
      setDivisionsList([]);
    } catch (error) {
        console.error(error);
        const msg = error.response?.data?.error || "שגיאה בהעלאת הקובץ";
        toast.error(msg);
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="upload-box">
      
      {/* בחירת ליגה */}
      {type !== "upcoming" && (
        <div className="mb-3">
          <label className="mr-2">בחר ליגה:</label>
          <select
            value={leagueCode}
            onChange={handleLeagueChange}
            className="select-league"
          >
            <option value="">-- בחר ליגה --</option>
            {leaguesList.map(lg => (
              <option key={lg._id} value={lg.code}>
                {lg.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* בחירת שלב */}
      {(type === "matches" || type === "ranking") && stagesList.length > 0 && (
      <div className="mb-3">
        <label className="mr-2">בחר שלב:</label>
        <select
          value={selectedStage}
          onChange={handleStageChange}
          className="select-stage"
        >
          <option value="">-- בחר שלב --</option>
          {stagesList.map(stage => (
            <option key={stage._id} value={stage._id}>
              {stage.name}
            </option>
          ))}
        </select>
      </div>
      )}

      {/* בחירת בית - יוצג רק אם זה דירוג */}
      {type === "ranking" && divisionsList.length > 0 && (
        <div className="mb-3">
          <label className="mr-2">בחר בית:</label>
          <select
            value={division}
            onChange={(e) => setDivision(e.target.value)}
            className="select-division"
          >
            <option value="">-- בחר בית --</option>
            {divisionsList.map((div) => (
              <option key={div._id} value={div._id}>
                {div.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="mb-3">
        <input type="file" accept=".csv" onChange={handleFileChange} />
      </div>

      <button
        onClick={handleUpload}
        disabled={loading}
        className="upload-btn"
      >
        {loading ? "מעלה..." : "העלאת קובץ"}
      </button>
    </div>
  );
};

export default UploadCSV;
